import { useRewardPools } from '../hooks/use-api';
import { Spinner } from '../components/shared/Spinner';
import { EmptyState } from '../components/shared/EmptyState';
import styles from './PoolsPage.module.css';

const POOL_INFO: ReadonlyArray<{ readonly title: string; readonly description: string }> = [
  { title: 'core / belief / trace', description: 'Filled by memory upkeep overflow, consolidation activity, and wager resolution rake.' },
  { title: 'peak', description: 'Mentorship rewards and significant experiences generate deposits here.' },
  { title: 'Distribution', description: 'Mentorship, autonomous earning.' },
  { title: 'Integrity', description: 'Atomic, overdraw-safe operations.' },
];

const PoolsPage = (): React.ReactElement => {
  const { data, isLoading, isError, error } = useRewardPools();

  const pools = data?.pools ?? [];
  const totalBalance = pools.reduce((sum, p) => sum + p.balance, 0);
  const activeCount = pools.filter((p) => p.balance > 0).length;
  const dormantCount = pools.length - activeCount;

  return (
    <div className={styles.container}>
      <h1 className={styles.title}>Reward Pools</h1>
      <p className={styles.description}>A portion of wagering activity flows into shared reward pools, supporting mentorship and autonomous earning.</p>

      {isLoading && (
        <div className={styles.loadingState}>
          <Spinner />
        </div>
      )}

      {isError && (
        <div className={styles.errorState}>
          <p className={styles.errorText}>
            Failed to load reward pools{error instanceof Error ? `: ${error.message}` : ''}
          </p>
        </div>
      )}

      {data && pools.length === 0 && (
        <EmptyState message="No reward pools found." />
      )}

      {data && pools.length > 0 && (
        <>
          <div className={styles.summary}>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Total Balance</span>
              <span className={styles.summaryValue}>{totalBalance.toLocaleString()} MC</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Active</span>
              <span className={styles.summaryValue}>{activeCount}</span>
            </div>
            <div className={styles.summaryItem}>
              <span className={styles.summaryLabel}>Dormant</span>
              <span className={styles.summaryValue}>{dormantCount}</span>
            </div>
          </div>

          <div className={styles.poolGrid}>
            {pools.map((pool) => {
              const maxValue = Math.max(pool.totalDeposited, 1);
              const balancePct = Math.min((pool.balance / maxValue) * 100, 100);
              return (
                <div key={pool.denomination} className={styles.poolCard}>
                  <h2 className={styles.poolDenomination}>{pool.denomination}</h2>
                  <div className={styles.poolStats}>
                    <div className={styles.poolStat}>
                      <span className={styles.poolStatLabel}>Balance</span>
                      <span className={styles.poolStatValue}>{pool.balance.toLocaleString()}</span>
                    </div>
                    <div className={styles.poolStat}>
                      <span className={styles.poolStatLabel}>Deposited</span>
                      <span className={styles.poolStatValue}>{pool.totalDeposited.toLocaleString()}</span>
                    </div>
                    <div className={styles.poolStat}>
                      <span className={styles.poolStatLabel}>Distributed</span>
                      <span className={styles.poolStatValue}>{pool.totalDistributed.toLocaleString()}</span>
                    </div>
                  </div>
                  <div className={styles.barContainer}>
                    <div className={styles.barFill} style={{ width: `${balancePct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {data && (
        <div className={styles.loopCallout}>
          <span className={styles.loopLabel}>The Loop</span>
          <p className={styles.loopText}>
            Every wager funds the commons. {totalBalance.toLocaleString()} MC redistributed so far. {pools.reduce((s, p) => s + p.balance, 0).toLocaleString()} MC waiting in pools.
          </p>
        </div>
      )}

      {/* How Pools Fill */}
      <section className={styles.howPoolsFill}>
        <h3 className={styles.howPoolsTitle}>How Pools Fill</h3>
        <div className={styles.howPoolsGrid}>
          {POOL_INFO.map((info) => (
            <div key={info.title} className={styles.howPoolsItem}>
              <div className={styles.howPoolsItemTitle}>{info.title}</div>
              <div className={styles.howPoolsItemDesc}>{info.description}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export { PoolsPage };
